import Link from 'found/Link';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import {
  Card, CardHeader, Col, Row, Table,
} from 'reactstrap';

import BadgeActive from 'src/components/Badge/BadgeActive';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';

import AdminTariffShowVolumeCharge from './AdminTariffShowVolumeCharge';

/**
 * Description
 * @param {object} props
 * @returns {React.ReactElement} - AdminTariffShow component
 */
function AdminTariffShow(props) {
  const intl = useIntl();

  if (!props) {
    return <Loading />;
  }

  const { tariff } = props;
  const {
    uuid, identifier, externalIdentifier, title,
    tariffType, active, createdAt, updatedAt,
    meter, property, volumeCharges,
  } = tariff;
  const { start, finish } = active;
  const {
    uuid: meterId, identifier: meterIdentifier, active: meterActive,
  } = meter;
  const {
    uuid: propertyId, title: propertyTitle, active: propertyActive,
  } = property;

  // i18n translations
  // Page title
  const pageTitle = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.page_title',
    defaultMessage: 'Enosi Admin - Tariff - {title}',
  }, { title });

  // Breadcrumbs
  const breadcrumbAdmin = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.breadcrumb.admin',
    defaultMessage: 'Admin',
  });
  const breadcrumbTariffs = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.breadcrumb.tariffs',
    defaultMessage: 'Tariffs',
  });

  // Card headers
  const tariffDetailsHeader = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.tariff_details.header',
    defaultMessage: 'Tariff details',
  });
  const volumeChargesHeader = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.volume_charges.header',
    defaultMessage: 'Volume charges',
  });

  // Table labels
  const activeStatusLabel = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.active_status.label',
    defaultMessage: 'Active status',
  });
  const idLabel = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.id.label',
    defaultMessage: 'ID',
  });
  const externalIdentifierLabel = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.external_identifier.label',
    defaultMessage: 'Ext ID',
  });
  const externalIdentifierTitle = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.external_identifier.title',
    defaultMessage: 'External identifier',
  });
  const startLabel = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.start.label',
    defaultMessage: 'Start',
  });
  const finishLabel = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.finish.label',
    defaultMessage: 'Finish',
  });
  const titleLabel = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.title.label',
    defaultMessage: 'Title',
  });
  const tariffTypeLabel = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.tariff_type.label',
    defaultMessage: 'Tariff type',
  });
  const identifierLabel = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.identifier.label',
    defaultMessage: 'Identifier',
  });
  const meterLabel = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.meter.label',
    defaultMessage: 'Meter',
  });
  const propertyLabel = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.property.label',
    defaultMessage: 'Property',
  });
  const createdAtLabel = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.created_at.label',
    defaultMessage: 'Created at',
  });
  const updatedAtLabel = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.updated_at.label',
    defaultMessage: 'Updated at',
  });

  // Volume charges table headers
  const volumeChargeTitleHeader = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.volume_charge.title.header',
    defaultMessage: 'Title',
  });
  const volumeChargeIdentifierHeader = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.volume_charge.identifier.header',
    defaultMessage: 'Identifier',
  });
  const volumeChargeIdHeader = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.volume_charge.id.header',
    defaultMessage: 'ID',
  });
  const volumeChargeExternalIdentifierHeader = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.volume_charge.external_identifier.header',
    defaultMessage: 'Ext ID',
  });
  const volumeChargeExternalIdentifierTitle = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.volume_charge.external_identifier.title',
    defaultMessage: 'External identifier',
  });
  const volumeChargeRateHeader = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.volume_charge.rate.header',
    defaultMessage: 'Rate',
  });
  const volumeChargeFlagsHeader = intl.formatMessage({
    id: 'components.admin.admin_tariff.admin_tariff_show.volume_charge.flags.header',
    defaultMessage: 'Flags',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbTariffs, path: '/admin/tariffs' },
          { name: title },
        ]}
      />

      <h1>
        {title}
      </h1>

      <Row className="clear-both">
        <Col xs={12} sm={6} md={4}>
          <Card>
            <CardHeader>
              {tariffDetailsHeader}
            </CardHeader>
            <Table responsive>
              <tbody>
                <tr>
                  <th scope="row" className="text-right">{activeStatusLabel}</th>
                  <td>
                    <BadgeActive start={start} finish={finish} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{idLabel}</th>
                  <td>
                    <UUID uuid={uuid} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">
                    <abbr title={externalIdentifierTitle}>
                      {externalIdentifierLabel}
                    </abbr>
                  </th>
                  <td>{externalIdentifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{startLabel}</th>
                  <td>
                    {start && DateTime.fromSeconds(start).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{finishLabel}</th>
                  <td>
                    {finish && DateTime.fromSeconds(finish).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{titleLabel}</th>
                  <td>{title}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tariffTypeLabel}</th>
                  <td>{tariffType}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{identifierLabel}</th>
                  <td>{identifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{meterLabel}</th>
                  <td>
                    <Link to={`/admin/meters/${meterId}`}>
                      <strong>{meterIdentifier}</strong>
                    </Link>
                    <br />
                    <UUID uuid={meterId} />
                    <br />
                    <BadgeActive start={meterActive.start} finish={meterActive.finish} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{propertyLabel}</th>
                  <td>
                    <Link to={`/admin/properties/${propertyId}`}>
                      <strong>{propertyTitle}</strong>
                    </Link>
                    <br />
                    <UUID uuid={propertyId} />
                    <br />
                    <BadgeActive start={propertyActive.start} finish={propertyActive.finish} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{createdAtLabel}</th>
                  <td>
                    {createdAt
                      && DateTime.fromSeconds(createdAt).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{updatedAtLabel}</th>
                  <td>
                    {updatedAt
                      && DateTime.fromSeconds(updatedAt).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={8}>
          <Card>
            <CardHeader>
              {volumeChargesHeader}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col">{volumeChargeTitleHeader}</th>
                  <th scope="col">{volumeChargeIdentifierHeader}</th>
                  <th scope="col">{volumeChargeIdHeader}</th>
                  <th scope="col">
                    <abbr title={volumeChargeExternalIdentifierTitle}>
                      {volumeChargeExternalIdentifierHeader}
                    </abbr>
                  </th>
                  <th scope="col">{volumeChargeRateHeader}</th>
                  <th scope="col">{volumeChargeFlagsHeader}</th>
                </tr>
              </thead>
              <tbody>
                {volumeCharges && volumeCharges.edges.map((el) => (
                  <AdminTariffShowVolumeCharge volumeCharge={el.node} key={el.node.uuid} />
                ))}
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>
    </>
  );
}

AdminTariffShow.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  tariff: PropTypes.shape({
    uuid: PropTypes.string,
    identifier: PropTypes.string,
    externalIdentifier: PropTypes.string,
    title: PropTypes.string,
    tariffType: PropTypes.string,
    createdAt: PropTypes.number,
    updatedAt: PropTypes.number,
    active: PropTypes.shape({
      start: PropTypes.number,
      finish: PropTypes.number,
    }),
    meter: PropTypes.shape({
      uuid: PropTypes.string,
      identifier: PropTypes.string,
      active: PropTypes.shape({
        start: PropTypes.number,
        finish: PropTypes.number,
      }),
    }),
    property: PropTypes.shape({
      uuid: PropTypes.string,
      title: PropTypes.string,
      active: PropTypes.shape({
        start: PropTypes.number,
        finish: PropTypes.number,
      }),
      address: PropTypes.shape({
        line1: PropTypes.string,
        line2: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        postcode: PropTypes.string,
      }),
    }),
    volumeCharges: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            uuid: PropTypes.string,
            identifier: PropTypes.string,
            externalIdentifier: PropTypes.string,
            title: PropTypes.string,
            rate: PropTypes.number,
            importFlag: PropTypes.bool,
            exportFlag: PropTypes.bool,
            ignorePublicHolidays: PropTypes.bool,
          }),
        }),
      ),
    }),
  }),
};

AdminTariffShow.defaultProps = {
  tariff: null,
};

export default createFragmentContainer(
  AdminTariffShow,
  {
    tariff: graphql`
      fragment AdminTariffShow_tariff on Tariff {
        uuid
        identifier
        externalIdentifier
        title
        tariffType
        createdAt
        updatedAt
        active { start finish }
        meter {
          uuid
          identifier
          active { start finish }
        }
        property {
          uuid
          title
          active { start finish }
        }
        volumeCharges(first: 500) {
          edges {
            node {
              uuid
              identifier
              externalIdentifier
              title
              rate
              importFlag
              exportFlag
              ignorePublicHolidays
            }
          }
        }
      }
    `,
  },
);
